/**
 * document-number-preview.ts — pure helper do live preview numeru dokumentu.
 *
 * Mirroruje logikę parsowania maski z `apps/api/lib/documents/numbering.ts`
 * (`parseMaskToSegments` + `buildNumber`), ale działa po stronie frontendu
 * bez round-tripu do backendu. Używany w `DocumentTypeFormModal` do
 * natychmiastowego podglądu formatu wpisanego przez użytkownika.
 *
 * **WAŻNE:** Zachowanie musi pozostać wizualnie spójne z silnikiem backendowym
 * (`apps/api/lib/documents/numbering.ts`). Przy każdej zmianie tamtego silnika
 * zaktualizuj też tę funkcję i jej testy.
 *
 * Tokeny WorkFlow-style (longest-match, od lewej):
 *   `#YYYY`    → rok 4-cyfrowy
 *   `#YY`      → rok 2-cyfrowy (po sprawdzeniu #YYYY)
 *   `#MM`      → miesiąc 2-cyfrowy
 *   `#N…N`     → licznik zero-padded do N cyfr (np. `#NNNN` → padding 4)
 *   inne znaki → literał
 *
 * Samotny `#` niezaczynający żadnego tokenu → literał `#`.
 *
 * @module
 */

/** Domyślna przykładowa data: 2026-06-15 UTC. */
const SAMPLE_DATE = new Date(Date.UTC(2026, 5, 15)); // miesiące 0-indexed

/**
 * Renderuje przykładowy numer dokumentu na podstawie maski WorkFlow-style.
 *
 * @param mask - maska numeracji (np. `'FZ/#YYYY/#MM/#NNNN'`)
 * @param sampleCounter - przykładowy licznik (domyślnie 1)
 * @param sampleDate - przykładowa data (domyślnie 2026-06-15 UTC)
 * @returns wyrenderowany numer, `''` gdy maska pusta/whitespace, lub `null` gdy format
 *   jest niepoprawny (licznik `#N…` przekracza 10 cyfr — backend odrzuca taki format)
 *
 * @example
 * previewDocumentNumber('FZ/#YYYY/#MM/#NNNN') // → 'FZ/2026/06/0001'
 * previewDocumentNumber('#YY-#NNNNNN')         // → '26-000001'
 * previewDocumentNumber('')                    // → ''
 */
export function previewDocumentNumber(
  mask: string,
  sampleCounter: number = 1,
  sampleDate: Date = SAMPLE_DATE,
): string | null {
  if (!mask?.trim()) return '';

  const year4 = String(sampleDate.getUTCFullYear());
  const year2 = year4.slice(-2);
  const month = String(sampleDate.getUTCMonth() + 1).padStart(2, '0');

  let result = '';
  let i = 0;

  while (i < mask.length) {
    if (mask[i] !== '#') {
      result += mask[i];
      i++;
      continue;
    }

    // Za '#' — sprawdzamy tokeny (longest-match: #YYYY przed #YY)
    const rest = mask.slice(i + 1);

    if (rest.startsWith('YYYY')) {
      result += year4;
      i += 5; // '#' + 'YYYY'
    } else if (rest.startsWith('YY')) {
      result += year2;
      i += 3; // '#' + 'YY'
    } else if (rest.startsWith('MM')) {
      result += month;
      i += 3; // '#' + 'MM'
    } else if (rest.startsWith('N')) {
      // Liczymy ciągłą serię N
      let nCount = 0;
      while (nCount < rest.length && rest[nCount] === 'N') {
        nCount++;
      }
      // Backend RZUCA przy >10 cyfr licznika (parseMaskToSegments) — sygnalizujemy
      // niepoprawny format przez null, by podgląd nie pokazał numeru, którego zapis odrzuci.
      if (nCount > 10) return null;
      result += String(sampleCounter).padStart(nCount, '0');
      i += 1 + nCount; // '#' + N×'N'
    } else {
      // Samotny '#' — literał
      result += '#';
      i++;
    }
  }

  return result;
}

/**
 * Waliduje maskę numeracji po stronie klienta — mirror backendowego
 * `validateNumberingMask` (`apps/api/lib/documents/numbering.ts`). Daje natychmiastową
 * informację zwrotną w edytorze typu, bez round-tripu (backend i tak waliduje ponownie).
 *
 * Pusta/whitespace = OK (silnik użyje domyślnej maski z licznikiem).
 *
 * @returns `'invalid'` gdy format się nie parsuje (np. licznik > 10 cyfr),
 *          `'noCounter'` gdy brak segmentu licznika (`#N…`), `null` gdy maska jest OK.
 */
export function numberingMaskError(mask: string): 'invalid' | 'noCounter' | null {
  if (!mask?.trim()) return null;
  // previewDocumentNumber zwraca null dokładnie dla nieparsowalnej maski (licznik > 10 cyfr).
  if (previewDocumentNumber(mask) === null) return 'invalid';
  // Segment licznika powstaje, gdy po '#' następuje 'N' — mirror parseMaskToSegments.
  if (!/#N/.test(mask)) return 'noCounter';
  return null;
}
